import queryString from 'querystring';
import {
  mapAndSortArrivals,
  mapVehicleStopsAndArrivals,
  filterToNearbyArrivals,
  filterToFavoriteArrivals,
} from '#/transit-tracker/utils/arrivals';
import {
  resetUserArrivals,
  fetchArrivalsError,
  fetchArrivalsSuccess,
  setLocationArrivalsEmpty,
  clearLocationArrivals,
  fetchLocationArrivalsSuccess,
  setUserArrivalsEmpty,
  fetchVehicleRouteSuccess,
  fetchVehicleRouteEmpty,
} from '#/transit-tracker/slice';
import { handleHttpResponse, handleGlobalError } from '#/shared/utils/http';
import {
  DEFAULT_ARRIVALS_QUERY,
  api as developerApiConfig,
} from '#/services/developerApi';

const fetchArrivals = async (locIDs, params = {}) => {
  const { appId, host, paths } = developerApiConfig;

  return fetch(
    `${host}${paths.arrivals}?${queryString.encode({
      ...DEFAULT_ARRIVALS_QUERY,
      locIDs,
      ...params,
      appId,
    })}`
  )
    .then(handleHttpResponse)
    .catch(handleGlobalError);
};

const batchFetchArrivals = async stops => {
  const MAX_STOPIDS_PER_REQUEST = 10;
  const arrivalQueries = [];
  for (let i = 0; i < stops.length; i += MAX_STOPIDS_PER_REQUEST) {
    // Rate limit Trimet API. At most ask for 40 stops arrivals
    if (arrivalQueries.length < 4) {
      arrivalQueries.push(
        fetchArrivals(
          stops
            .slice(i, i + MAX_STOPIDS_PER_REQUEST)
            .map(s => s.stopId || s.locid)
            .join(),
          { detailLevel: 2 }
        )
      );
    }
  }

  const results = await Promise.all(arrivalQueries).then(result =>
    result.reduce((rs, rsNew) => [...rs, rsNew.resultSet], [])
  );
  return {
    arrivals: results
      .map(rs => rs.arrival)
      .reduce((arr, val) => [...arr, ...val], []),
    routes: results
      .map(rs => rs.route)
      .reduce((arr, val) => [...arr, ...val], []),
  };
};

// Update arrivals for stops nearby user
const updateNearbyArrivals = async (
  dispatch,
  getState,
  currentLocation,
  nearbyStops = []
) => {
  const { favoriteStops } = getState().transitTracker;

  if (currentLocation === null) {
    dispatch(resetUserArrivals());
  } else if (nearbyStops.length === 0) {
    dispatch(setUserArrivalsEmpty());
  }

  const allStops = [
    // there's a limit on the number of stops for which arrivals will be
    // requested so put favorites at the start of the array so that they
    // aren't truncated
    ...(favoriteStops || []),
    ...nearbyStops,
  ];

  if (allStops.length === 0) {
    return;
  }

  let results;
  try {
    results = await batchFetchArrivals(allStops);
  } catch {
    dispatch(fetchArrivalsError());
    return;
  }

  const mappedArrivals = mapAndSortArrivals(
    results.arrivals,
    allStops,
    results.routes
  );

  dispatch(
    fetchArrivalsSuccess({
      nearbyArrivals: filterToNearbyArrivals(mappedArrivals, nearbyStops),
      favorites: filterToFavoriteArrivals(mappedArrivals, favoriteStops),
    })
  );
};

// Update arrivals at a location
const updateLocationArrivals = async (dispatch, getState, stopsData = []) => {
  // TODO: Is this redundant with getting the nearbyStops from the transit tracker above?
  let stops = [];
  stops = stopsData;

  if (stops.length === 0) {
    // make location specific
    dispatch(setLocationArrivalsEmpty());
  }

  if (stops.length === 0) {
    return;
  }

  let results;
  try {
    results = await batchFetchArrivals(stops);
  } catch {
    dispatch(fetchArrivalsError());
    return;
  }

  const mappedArrivals = mapAndSortArrivals(
    results.arrivals,
    stops,
    results.routes
  );
  dispatch(
    fetchLocationArrivalsSuccess({
      nearby: filterToNearbyArrivals(mappedArrivals, stops),
    })
  );
};

export const removeLocationArrivals = () => async dispatch => {
  dispatch(clearLocationArrivals());
};

export const updateArrivalsNearCurrentLocation =
  (currentLocation, nearbyStops) => async (dispatch, getState) => {
    updateNearbyArrivals(dispatch, getState, currentLocation, nearbyStops);
  };

export const updateArrivalsNearSelectedLocation =
  stopsData => async (dispatch, getState) => {
    updateLocationArrivals(dispatch, getState, stopsData);
  };

export const updateVehicleRoute = (
  route,
  vehicleId,
  vehicleDir,
  lastLocId,
  nextLocId
) => {
  return async dispatch => {
    if (!route || !nextLocId) {
      dispatch(fetchVehicleRouteEmpty());
      return;
    }

    // since the routeStops come from the redux state they can't be mutated
    // so a copy of their list must be made in order to sort them
    const routeStops = [...route.dir[vehicleDir].stop];
    routeStops.sort((a, b) => a.seq - b.seq);

    // If the lastLocId is null then the bus has not made its first stop
    const firstStopIndex = lastLocId
      ? routeStops.findIndex(s => String(s.locid) === String(lastLocId))
      : 0;

    const vehicleStops = routeStops.slice(firstStopIndex);

    let arrivalsResponse;
    try {
      arrivalsResponse = await batchFetchArrivals(vehicleStops);
    } catch {
      dispatch(fetchArrivalsError());
      return;
    }
    const arrivals = (arrivalsResponse.arrivals || []).filter(
      a => String(a.vehicleID) === String(vehicleId)
    );

    dispatch(
      fetchVehicleRouteSuccess({
        ...route,
        stops: mapVehicleStopsAndArrivals(vehicleStops, arrivals),
      })
    );
  };
};
